import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, ShieldX, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * Unauthorized
 *
 * Route: /unauthorized (rendered by ProtectedRoute when access is denied)
 *
 * 403 error page shown when a user tries to access a route they are not
 * allowed to view. Two scenarios are handled:
 *   - Not logged in: primary action is "Sign In" → "/login".
 *   - Logged in but lacking permission (e.g. pending approval, not admin):
 *     primary action is "Back to Dashboard" → "/dashboard".
 *
 * The logged-in check is a quick localStorage token presence test — it does
 * not verify the token's validity, only whether one exists.
 *
 * The page uses framer-motion for entrance animations and continuously
 * animated background blobs.
 */
export default function Unauthorized() {
  // Quick check: if a JWT token is present in localStorage, the user is considered logged in.
  // This determines the message text and which primary action button to show.
  const isLoggedIn = !!localStorage.getItem('token');

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 relative overflow-hidden">
      {/* Animated background elements */}
      <motion.div
        animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 5, 0]
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="absolute top-20 left-10 w-[400px] h-[400px] bg-gradient-to-br from-red-300/20 to-orange-300/20 dark:from-red-500/5 dark:to-orange-500/5 rounded-full blur-3xl"
      />
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, -5, 0]
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
        className="absolute bottom-10 right-10 w-[500px] h-[500px] bg-gradient-to-br from-orange-300/20 to-red-300/20 dark:from-orange-500/5 dark:to-red-500/5 rounded-full blur-3xl"
      />

      <div className="relative text-center space-y-8 max-w-lg">
        {/* 403 Number */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.5,
            type: "spring",
            stiffness: 200
          }}
        >
          <h1 className="text-[150px] md:text-[200px] font-bold leading-none text-transparent bg-clip-text bg-gradient-to-br from-red-500 via-orange-500 to-yellow-500">
            403
          </h1>
        </motion.div>

        {/* Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-4"
        >
          <h2 className="text-3xl md:text-4xl font-bold">Access Denied</h2>
          <p className="text-lg text-muted-foreground">
            {/* Show a different message depending on whether the user is logged in. */}
            {isLoggedIn
              ? "You don't have permission to access this page. Please contact an administrator if you believe this is an error."
              : "You need to be logged in to access this page."
            }
          </p>
        </motion.div>

        {/* Shield Icon Animation */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex justify-center"
        >
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="p-4 bg-red-100 dark:bg-red-900/20 rounded-full"
          >
            <ShieldX className="h-8 w-8 text-red-500" />
          </motion.div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4"
        >
          {/* Primary action depends on login state */}
          {!isLoggedIn ? (
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button size="lg" asChild className="px-6 h-12">
                <Link to="/login" className="flex items-center gap-2">
                  <LogIn className="h-4 w-4" />
                  Sign In
                </Link>
              </Button>
            </motion.div>
          ) : (
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button size="lg" asChild className="px-6 h-12">
                <Link to="/dashboard" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Dashboard
                </Link>
              </Button>
            </motion.div>
          )}

          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button size="lg" variant="outline" asChild className="px-6 h-12">
              <Link to="/" className="flex items-center gap-2">
                <Home className="h-4 w-4" />
                Go Home
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
